import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Protected Route Component
 * Blocks access to routes based on user permissions
 * 
 * Usage:
 * <Route path="/clients" element={
 *   <ProtectedRoute requiredPermission="client.read">
 *     <ClientsPage />
 *   </ProtectedRoute>
 * } />
 */
export const ProtectedRoute = ({ 
  children, 
  requiredPermission,
  requiredPermissions,
  requiredMenuKey,
  requiredMenuKeys,
  requiredMenuAction = 'view',
  requireAll = false,
  redirectTo = '/login',
  fallback = null
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasMenuPermission, loading } = usePermissions();
  const location = useLocation();
  const token = localStorage.getItem('token');

  // If loading permissions, show nothing (or a loader)
  if (loading) {
    return <div className="flex items-center justify-center h-screen bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-primary"></div>
    </div>;
  }

  // If not authenticated, redirect to login
  if (!token) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (requiredMenuKey && !hasMenuPermission(requiredMenuKey, requiredMenuAction)) {
    return fallback || <Navigate to="/403" replace />;
  }

  if (requiredMenuKeys && requiredMenuKeys.length > 0 && !requiredMenuKeys.some((menuKey) => hasMenuPermission(menuKey, requiredMenuAction))) {
    return fallback || <Navigate to="/403" replace />;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback || <Navigate to="/403" replace />;
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);
    
    if (!hasAccess) {
      return fallback || <Navigate to="/403" replace />;
    }
  }

  // Permission granted, render children
  return children;
};

export default ProtectedRoute;
